import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { getJson, mutateJson } from '../api/client';
import { Field, Input, Select } from '../components/form';
import { Alert, Badge, Button, Card, EmptyState, Spinner } from '../components/ui';
import { dataBr, mesAno, parseReais } from '../lib/format';
import { ConferenciaLote } from './ConferenciaLote';
import styles from './screens.module.css';

interface Competencia {
  id: number;
  ano: number;
  mes: number;
}
type StatusLote = 'sem_csv' | 'a_importar' | 'em_conferencia' | 'conferido';
export interface Lote {
  id: number;
  competenciaId: number | null;
  competencia: { ano: number; mes: number } | null;
  periodoInicio: string | null;
  periodoFim: string | null;
  temCsv: boolean;
  temPdf: boolean;
  lancamentos: number;
  pendentes: number;
  fechado: boolean;
  status: StatusLote;
}
interface ResultadoImportacao {
  error?: string;
  total?: number;
  inseridos?: number;
  ignorados?: number;
}

type Aviso = { kind: 'success' | 'warning' | 'danger'; texto: string };

export const STATUS_LOTE: Record<StatusLote, string> = {
  sem_csv: 'Sem CSV',
  a_importar: 'A importar',
  em_conferencia: 'Em conferência',
  conferido: 'Conferido',
};
const MSG_ARQUIVO = 'Escolha o CSV ou o PDF do extrato.';
const MSG_SALDO = 'Informe o saldo em reais, como 1.234,56.';
/** Erros da API de envio/importação (código → mensagem), mostrados acima da lista. */
const ERRO_GERAL: Record<string, string> = {
  tenant_suspended: 'Condomínio suspenso — somente leitura.',
  invalid_mime_csv: 'O CSV precisa ser text/csv ou texto simples.',
  invalid_mime_pdf: 'O PDF precisa ser application/pdf.',
  file_too_large: 'O arquivo passa de 25 MB.',
  missing_file: MSG_ARQUIVO,
  competencia_not_found: 'Competência não encontrada.',
  invalid_competencia: 'Escolha a competência do extrato.',
  no_csv: 'Este lote não tem CSV para importar.',
};
const ERRO_GERAL_PADRAO = 'Não foi possível concluir. Tente novamente.';
const MSG_IMPORTACAO_FALHOU =
  'Extrato enviado, mas a importação falhou. Use "Importar" na lista para tentar de novo.';

/** "Extrato importado: N lançamentos novos, M ignorados por duplicidade." */
function resumoImportacao(r: ResultadoImportacao): string {
  const novos = r.inseridos ?? 0;
  const ignorados = r.ignorados ?? 0;
  const parteNovos = `${novos} ${novos === 1 ? 'lançamento novo' : 'lançamentos novos'}`;
  const parteIgnorados =
    ignorados === 0
      ? 'nenhum ignorado'
      : `${ignorados} ${ignorados === 1 ? 'ignorado' : 'ignorados'}`;
  return `Extrato importado: ${parteNovos}, ${parteIgnorados} por duplicidade.`;
}

/**
 * Conferência do extrato (RF-A09): lista de lotes, envio de CSV/PDF com importação
 * imediata e conferência dos lançamentos do lote escolhido. `editavel` só para o síndico
 * de condomínio ativo: envio, importação e o PATCH de categoria/conciliação são
 * `requireRole('sindico')`; o contábil e o tenant suspenso (#127) só leem.
 */
export function Conferencia({ editavel }: { editavel: boolean }) {
  const [comps, setComps] = useState<Competencia[]>([]);
  const [lotes, setLotes] = useState<Lote[] | 'erro' | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [aberto, setAberto] = useState<number | null>(null);
  // Muda a cada importação do lote aberto: remonta a conferência para reler os lançamentos.
  const [versao, setVersao] = useState(0);
  // Lote aberto no momento: a resposta de outro (clique rápido) é descartada.
  const abertoAtual = useRef(aberto);
  abertoAtual.current = aberto;

  const carregarLotes = useCallback(async () => {
    const dados = await getJson<{ extratos: Lote[] }>('/api/extratos');
    setLotes(dados ? dados.extratos : 'erro');
  }, []);

  useEffect(() => {
    void carregarLotes();
    void getJson<{ competencias: Competencia[] }>('/api/competencias').then((d) =>
      setComps(d?.competencias ?? []),
    );
  }, [carregarLotes]);

  /** Roda a importação (parse) do lote e devolve o aviso do resultado (sucesso ou falha). */
  async function importar(id: number, falhaPadrao: string): Promise<Aviso> {
    const res = await mutateJson<ResultadoImportacao>(
      `/api/extratos/${encodeURIComponent(id)}/parsear`,
      {},
    );
    if (!res.ok) {
      const codigo = res.data?.error;
      return { kind: 'danger', texto: (codigo && ERRO_GERAL[codigo]) || falhaPadrao };
    }
    if ((res.data?.total ?? 0) === 0) {
      return {
        kind: 'warning',
        texto: 'Nenhuma linha reconhecida no CSV: confira se é o extrato do Inter.',
      };
    }
    return { kind: 'success', texto: resumoImportacao(res.data ?? {}) };
  }

  async function importarDaLista(id: number) {
    if (ocupado) return;
    setOcupado(true);
    setAviso(null);
    const resultado = await importar(id, ERRO_GERAL_PADRAO);
    setOcupado(false);
    setAviso(resultado);
    await carregarLotes();
    // Se o lote importado é o que está aberto, os lançamentos mostrados ficaram velhos.
    if (abertoAtual.current === id) setVersao((v) => v + 1);
  }

  async function aoEnviar(resultado: Aviso) {
    setAviso(resultado);
    await carregarLotes();
  }

  if (lotes === null) return <Spinner />;
  const loteAberto = Array.isArray(lotes) ? (lotes.find((l) => l.id === aberto) ?? null) : null;

  return (
    <div className={styles.stack}>
      {aviso ? <Alert kind={aviso.kind}>{aviso.texto}</Alert> : null}

      {editavel ? (
        <EnvioExtrato
          comps={comps}
          ocupado={ocupado}
          setOcupado={setOcupado}
          importar={importar}
          onConcluido={aoEnviar}
        />
      ) : null}

      {lotes === 'erro' ? (
        <Alert kind="danger">Não foi possível carregar os lotes de extrato.</Alert>
      ) : lotes.length === 0 ? (
        <EmptyState>Nenhum extrato enviado ainda.</EmptyState>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Lote</th>
                <th>Competência</th>
                <th>Período</th>
                <th>Fonte</th>
                <th className={styles.num}>Lançamentos</th>
                <th className={styles.num}>Pendentes</th>
                <th>Situação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lotes.map((l) => (
                <tr key={l.id}>
                  <th scope="row">#{l.id}</th>
                  <td>{l.competencia ? mesAno(l.competencia) : '—'}</td>
                  <td>
                    {l.periodoInicio && l.periodoFim
                      ? `${dataBr(l.periodoInicio)} a ${dataBr(l.periodoFim)}`
                      : '—'}
                  </td>
                  <td>{l.temCsv ? (l.temPdf ? 'CSV + PDF' : 'CSV') : 'PDF'}</td>
                  <td className={styles.num}>{l.lancamentos}</td>
                  <td className={styles.num}>{l.pendentes}</td>
                  <td>
                    <div className={styles.row}>
                      <Badge tone={l.status === 'conferido' ? 'success' : undefined}>
                        {STATUS_LOTE[l.status]}
                      </Badge>
                      {l.fechado ? <span className="muted">Saldo confere</span> : null}
                    </div>
                  </td>
                  <td>
                    <div className={styles.row}>
                      <Button
                        size="sm"
                        variant="secondary"
                        aria-label={`Conferir lote ${l.id}`}
                        onClick={() => setAberto(l.id)}
                      >
                        Conferir
                      </Button>
                      {editavel && l.status === 'a_importar' ? (
                        <Button
                          size="sm"
                          disabled={ocupado}
                          aria-label={`Importar lote ${l.id}`}
                          onClick={() => void importarDaLista(l.id)}
                        >
                          Importar
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loteAberto ? (
        <ConferenciaLote
          key={`${loteAberto.id}-${versao}`}
          lote={loteAberto}
          editavel={editavel}
          onAlterado={() => void carregarLotes()}
        />
      ) : null}
    </div>
  );
}

/**
 * Envio de CSV e/ou PDF do extrato: cria o lote (`POST /api/extratos`) e, com CSV, roda a
 * importação na sequência. Tipo e tamanho inválidos voltam como erro da API (415/413).
 */
function EnvioExtrato({
  comps,
  ocupado,
  setOcupado,
  importar,
  onConcluido,
}: {
  comps: Competencia[];
  ocupado: boolean;
  setOcupado: (v: boolean) => void;
  importar: (id: number, falhaPadrao: string) => Promise<Aviso>;
  onConcluido: (aviso: Aviso) => Promise<void>;
}) {
  const [competencia, setCompetencia] = useState('');
  const [csv, setCsv] = useState<File | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [saldoInicial, setSaldoInicial] = useState('');
  const [saldoFinal, setSaldoFinal] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [erroSaldo, setErroSaldo] = useState<string | null>(null);
  // Troca a cada envio concluído para esvaziar os campos de arquivo (são não controlados).
  const [rodada, setRodada] = useState(0);

  // Competência escolhida; sem escolha usa a mais recente (a primeira da lista).
  const competenciaEscolhida = competencia || (comps[0] ? String(comps[0].id) : '');

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (ocupado) return;
    const centavosInicial = saldoInicial.trim() ? parseReais(saldoInicial) : undefined;
    const centavosFinal = saldoFinal.trim() ? parseReais(saldoFinal) : undefined;
    setErro(!csv && !pdf ? MSG_ARQUIVO : null);
    setErroSaldo(centavosInicial === null || centavosFinal === null ? MSG_SALDO : null);
    if ((!csv && !pdf) || centavosInicial === null || centavosFinal === null) return;

    const form = new FormData();
    if (csv) form.set('csv', csv);
    if (pdf) form.set('pdf', pdf);
    if (competenciaEscolhida) form.set('competenciaId', competenciaEscolhida);
    if (centavosInicial !== undefined) form.set('saldoInicial', String(centavosInicial));
    if (centavosFinal !== undefined) form.set('saldoFinal', String(centavosFinal));
    if (inicio) form.set('periodoInicio', inicio);
    if (fim) form.set('periodoFim', fim);

    setOcupado(true);
    const res = await mutateJson<{ error?: string; importExtrato?: { id: number } }>(
      '/api/extratos',
      form,
    );
    if (!res.ok) {
      setOcupado(false);
      const codigo = res.data?.error;
      await onConcluido({
        kind: 'danger',
        texto: (codigo && ERRO_GERAL[codigo]) || ERRO_GERAL_PADRAO,
      });
      return;
    }
    const id = res.data?.importExtrato?.id;
    let resultado: Aviso;
    if (!csv || id === undefined) {
      resultado = {
        kind: 'success',
        texto: 'PDF guardado. Sem CSV não há lançamentos para importar.',
      };
    } else {
      const importacao = await importar(id, MSG_IMPORTACAO_FALHOU);
      resultado = importacao.kind === 'success' ? importacao : { ...importacao, kind: 'warning' };
    }
    setOcupado(false);
    setCsv(null);
    setPdf(null);
    setRodada((r) => r + 1);
    await onConcluido(resultado);
  }

  return (
    <Card title="Enviar extrato">
      <form onSubmit={enviar} noValidate>
        <div className={styles.row}>
          <Field label="Competência">
            {(id) => (
              <Select
                id={id}
                value={competenciaEscolhida}
                onChange={(e) => setCompetencia(e.target.value)}
                style={{ maxWidth: '12rem' }}
              >
                {comps.map((c) => (
                  <option key={c.id} value={c.id}>
                    {mesAno(c)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Arquivo CSV" error={erro}>
            {(id) => (
              <Input
                key={rodada}
                id={id}
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(e) => setCsv(e.target.files?.[0] ?? null)}
              />
            )}
          </Field>
          <Field label="Arquivo PDF">
            {(id) => (
              <Input
                key={rodada}
                id={id}
                type="file"
                accept=".pdf,application/pdf"
                onChange={(e) => setPdf(e.target.files?.[0] ?? null)}
              />
            )}
          </Field>
        </div>
        <div className={styles.row}>
          <Field label="Saldo inicial (opcional)">
            {(id) => (
              <Input
                id={id}
                value={saldoInicial}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setSaldoInicial(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="Saldo final do extrato (opcional)" error={erroSaldo}>
            {(id) => (
              <Input
                id={id}
                value={saldoFinal}
                inputMode="decimal"
                placeholder="0,00"
                onChange={(e) => setSaldoFinal(e.target.value)}
                style={{ maxWidth: '10rem' }}
              />
            )}
          </Field>
          <Field label="Início do período (opcional)">
            {(id) => (
              <Input
                id={id}
                type="date"
                value={inicio}
                onChange={(e) => setInicio(e.target.value)}
              />
            )}
          </Field>
          <Field label="Fim do período (opcional)">
            {(id) => (
              <Input id={id} type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
            )}
          </Field>
        </div>
        <p className="muted">
          Sem o saldo final, o passo de extrato do fechamento do mês não fecha.
        </p>
        <Button type="submit" disabled={ocupado}>
          Enviar e importar
        </Button>
      </form>
    </Card>
  );
}
